/*
 * postLoginRoute — decide where to send a user right after auth lands.
 *
 * RULE: /orbit/intake is the FIRST-TIME onboarding surface. It is shown ONLY
 * when an account is just created (a signup). A RETURNING login (existing
 * account / already-registered Google email) always lands on /dashboard — even
 * if onboarding/L1 is incomplete; the dashboard surfaces any pending intake as
 * a to-do. A consultant goes to the Solution Partner console; a super to the
 * Command Deck.
 *
 * Why `isNew` and not L1 progress: the previous version fetched
 * /api/intake/state and keyed the decision on whether Layer 1 was complete, so
 * a returning user who bounced mid-intake was bounced straight back to
 * /orbit/intake on every login — the exact bug. The authoritative signup signal
 * is the backend `is_new` boolean: it is true ONLY when the user row was just
 * INSERTed (oauth.py `_find_or_create_user` / email signup), false for every
 * existing user. Google OAuth forwards it on the callback URL. Email login sets
 * it on one case only: a client's first sign-in to an account someone else made
 * for them (a partner's Add a client emails a password, so that person never
 * signed up or accepted an invite). Every later login is a returning user.
 *
 * Kept async so existing `await` / `.then()` call sites are untouched. No
 * network call is made.
 */


export async function resolvePostLoginRoute(
  role: string | null | undefined,
  clientId: string | null | undefined,
  isNew: boolean = false,
): Promise<string> {
  // Provider is an additive capability, not a different home — a provider
  // member is a client and lands on the client dashboard like any client (the
  // extra Provider section appears in their sidebar).
  // The super lands on the Command Deck (redirects to Business overview).
  if (role === 'super') {
    return '/super/command-deck';
  }
  // A consultant is a Solution Partner: their home is the client console, and
  // they never see the client onboarding surface.
  if (role === 'consultant') {
    // A partner who just accepted their invite with Google starts on the welcome deck.
    if (isNew) return '/partner/welcome';
    return '/partner/framework';
  }
  // E2: no 'expert' branch. Expert is a flag on a member (is_expert), never a
  // login role, and /expert only redirected to /dashboard anyway. A provider
  // member reaches the builder through the sidebar link, which reads the flag.
  // Brand-new account (first time) → onboarding. Everyone else → dashboard.
  // A brand-new account walks the setup, as an email sign-up does.
  if (isNew) {
    return '/setup';
  }
  // A gated route stashed the URL the user was ACTUALLY headed to before the
  // login wall (RoleBasedRoute sets aa_return_to). Honor it for client roles:
  // dropping it silently ate Stripe success returns' query params (ds_session)
  // and turned real payments into "paid → dashboard". Same-origin paths only —
  // never a full URL — so this can't become an open redirect.
  try {
    const returnTo = sessionStorage.getItem('aa_return_to');
    if (returnTo) {
      sessionStorage.removeItem('aa_return_to');
      if (returnTo.startsWith('/') && !returnTo.startsWith('//') && !returnTo.startsWith('/login')) {
        return returnTo;
      }
    }
  } catch { /* storage unavailable — default route below */ }
  // The root route sends every client role to Home. The Room is a page they
  // open from there, not a landing.
  return '/';
}
